import type { RouteLocationNormalized } from "vue-router";
import type { ICustomRouter, IRouteGuard } from "@library/Services";
import { useJupiterProps, type IJupiterProps } from "..";
import { useUser } from "@domain/Security/User";
import { useReturnToUserSetting } from "@library/Types/UserSettings";

export class JupiterRouteGuard implements IRouteGuard
{
    
    /**
     * Validates the route navigation, handling exceptions or allowing the navigation to proceed
     * @param to 
     * @param router 
     * @returns 
     */
    async validateRoute( to: RouteLocationNormalized, router: ICustomRouter )
    {

        // Await the jupiter props being ready - this will ensure that there are properties
        // and that there has been an attempt to authenticate the user against the api using
        // those properties.  Once authentication has been attempted, if unsuccessful, the
        // condition will be handled by the handleNotAuthenticated() step.
        const jupiterProps = useJupiterProps();
        await jupiterProps.isReady();

        return this.handleNotAuthenticated( to, router, jupiterProps )
            ?? this.handleRequiresWelcomeFlow( to, router, jupiterProps )
            ?? this.handleVehicleSetupIncomplete( to, jupiterProps )
            ?? this.handleInvalidNavigation( to, jupiterProps );
    
    }

    /**
     * Handles the user navigating to a page that requires authentication, but they are not authenticated
     * @param to 
     * @param router 
     * @param jupiterProps 
     * @returns 
     */
    private handleNotAuthenticated( to: RouteLocationNormalized, router: ICustomRouter, jupiterProps: IJupiterProps )
    {

        const requiresAuth = to.meta?.allowAnonymous == false || to.meta?.allowAnonymous == null;

        // if the destination page requires authentication but the user is not logged in,
        // redirect them to login
        if( requiresAuth && jupiterProps.isAuthenticated == false ) 
        {
            let returnTo = useReturnToUserSetting();
            router.redirectedFromPath = to.fullPath;
            returnTo.value = { name: router.navigation.current.name, path: router.navigation.current.fullPath };
            return { name: 'Login' };
        }

        return null; // null signifies that the navigation is valid

    }

    /**
     * Handles the condition that a user requires the welcome flow, or that they 
     * @param to 
     * @param router 
     * @param jupiterProps 
     * @returns 
     */
    private handleRequiresWelcomeFlow( to: RouteLocationNormalized, router: ICustomRouter, jupiterProps: IJupiterProps )
    {

        const user = useUser();
        const requiresWelcomeFlow = user?.activeVehicle?.requiresWelcomeFlow;
        const isSharedVehicle = user?.activeVehicle?.isShared;

        if( jupiterProps.isAuthenticated && requiresWelcomeFlow && !isSharedVehicle && to.name?.toString().indexOf( 'Welcome Flow' ) != 0 )
        {
            let returnTo = useReturnToUserSetting();
            router.redirectedFromPath = to.fullPath;
            returnTo.value = { name: to.name as string, path: to.fullPath };
            
            return { name: 'Welcome Flow' };
        }
    
        return null; // null signifies that the navigation is valid
        
    }

    private handleVehicleSetupIncomplete( to: RouteLocationNormalized, jupiterProps: IJupiterProps )
    {
        if( jupiterProps.isAuthenticated && to.name == 'Vehicle Setup Incomplete' )
            return null;

        const user = useUser();
        const requiresWelcomeFlow = user?.activeVehicle?.requiresWelcomeFlow;
        const isSharedVehicle = user?.activeVehicle?.isShared;

        if( jupiterProps.isAuthenticated && isSharedVehicle && requiresWelcomeFlow )
        {
            return { name: 'Vehicle Setup Incomplete' };
        }
    
        return null; // null signifies that the navigation is valid
    }

    /**
     * Handles the user attempting to navigate to an invalid route, e.g. going to login when they are
     * already authenticated
     * @param to 
     * @param jupiterProps 
     * @returns 
     */
    private handleInvalidNavigation( to: RouteLocationNormalized, jupiterProps: IJupiterProps )
    {

        // prevent the user from navigating to login if they are already logged in
        if( jupiterProps.isAuthenticated && to.name == 'Login' )
        {
            return { name: 'My Garage' };
        }

        return null; // null signifies that the navigation is valid
        
    }

}
